import * as MapRepository from "repository/MapRepository";
import {buildMap} from "util/mapModel";
import {ONE_RETRY_LOCKED, savePolicy, withRetry} from "util/retry";
import {isDailyCap, mapReadLimiter} from "util/rateLimit";
import {settleAll} from "util/settle";

/**
 * Reads the maps of the windows that were open, side by side (the stored layout holds at most nine windows, kept ones included), through the read
 * limiter, so that a burst of 429s is waited out once by all of them. A map that fails does not stop the others: the answer names the failures, and the caller decides what each means for its window.
 * A map whose read the daily cap refused (429 map_read_daily_cap) is not a failure of the map: its name is in `kept`, for the caller to leave its window in
 * the layout, to be opened with the next project open.
 * @param {string} projectId
 * @param {string[]} names - the names of the maps
 * @returns {Promise<{loaded: Object<string, LoadedMap>, failed: {name: string, error: Error}[], kept: string[]}>} the maps that loaded, by name, the others, and
 *   the names of the maps the daily cap refused
 */
export const getMaps = async (projectId, names) => {
    const results = await settleAll(names.map(name => getMap(projectId, name)));
    const loaded = {};
    const failed = [];
    const kept = [];

    results.forEach((result, i) => {
        if (result.status === 'fulfilled') {
            loaded[names[i]] = result.value;
        } else if (isDailyCap(result.reason)) {
            kept.push(names[i]);
        } else {
            failed.push({name: names[i], error: result.reason});
        }
    });

    return {loaded, failed, kept};
}

/**
 * Reads one map, in one request, and builds it for the editor. The read goes through the limiter (util/rateLimit.js), the only one of the API's reads that
 * is counted (R48): a 429 burst is waited out and the read asked again once. `wait: false` is for a read made from inside a queue step: a pause or the daily cap
 * rejects at once with the 429 and no request is sent, so that a step never waits out a pause.
 * @param {string} projectId
 * @param {string} name - the name of the map
 * @param {{wait: boolean}} [options] - wait (true unless said) whether the read waits for a pause to end
 * @returns {Promise<LoadedMap>}
 * @throws {ApiError} 429 map_read_burst or map_read_daily_cap when the limiter gives up on the read, or any other failure of the request
 * @throws {UnreadableMapError} when the uploaded file holds no values for the map
 */
export const getMap = async (projectId, name, {wait = true} = {}) => {
    return buildMap(await mapReadLimiter.run(() => MapRepository.getMap(projectId, name), {wait}));
}

/**
 * Saves the values of a map's data, in one request (API 1.3), and builds the map the API answers with. A project that is busy with another change
 * (409 project_locked) is asked again three times and a request that got no response once (R41, savePolicy); the save is the whole map, so asking
 * again is safe. What the API answers with is kept whole as the map's `raw`: the values in it are the stored ones, re-decoded, which can differ from
 * what was sent, so the window shows the answer and not what the user typed.
 * @param {string} projectId
 * @param {string} name - the name of the map
 * @param {number[][]} values - the values of the map's data axis, exactly rows by columns
 * @param {{sleep: function(number): Promise, jitter: function(number): number}} [retryOptions] - withRetry's third argument; the tests give an instant sleep
 * @returns {Promise<{map: LoadedMap, affectedMaps: string[], affectedMapsState: Object<string, boolean>, undoBatchId: ?string, changedBytes: number}>}
 * @throws {ApiError|NoResponseError} the failure of the request, once the policy has no retry left for it
 * @throws {UnreadableMapError} when the answer holds no values for the uploaded file
 */
export const saveMap = async (projectId, name, values, retryOptions = {}) => {
    const raw = await withRetry(() => MapRepository.saveMap(projectId, name, values), savePolicy(), retryOptions);
    const {affectedMaps, affectedMapsState, undoBatchId, changedBytes} = raw;

    return {map: buildMap(raw), affectedMaps, affectedMapsState, undoBatchId, changedBytes};
}

/**
 * Saves the values of one of a map's axes, in one request (API 1.3), with the same policy as a data save: a project that is busy (409 project_locked)
 * is asked again three times and a request that got no response once, with the identical body. The answer is the map built from it, with the maps the
 * save affected and their flags (R46).
 * @param {string} projectId
 * @param {string} name - the name of the map
 * @param {'X'|'Y'} axis - the letter of the axis
 * @param {number[]} values - the flat list of the axis's values, exactly the size of the template row
 * @param {{sleep: function(number): Promise, jitter: function(number): number}} [retryOptions] - withRetry's third argument; the tests give an instant sleep
 * @returns {Promise<{map: LoadedMap, affectedMaps: string[], affectedMapsState: Object<string, boolean>, undoBatchId: ?string, changedBytes: number}>}
 * @throws {ApiError|NoResponseError} the failure of the request, once the policy has no retry left for it
 * @throws {UnreadableMapError} when the answer holds no values for the uploaded file
 */
export const saveAxis = async (projectId, name, axis, values, retryOptions = {}) => {
    const raw = await withRetry(() => MapRepository.saveAxis(projectId, name, axis, values), savePolicy(), retryOptions);
    const {affectedMaps, affectedMapsState, undoBatchId, changedBytes} = raw;

    return {map: buildMap(raw), affectedMaps, affectedMapsState, undoBatchId, changedBytes};
};

/**
 * Toggles the favourite star of a map (API 1.6). A project that is busy (409 project_locked) is asked again once, after a second; a request that got no
 * response is never asked again: a toggle is not idempotent, and the lost one may have been written.
 * @param {string} projectId
 * @param {string} name - the name of the map
 * @param {{sleep: function(number): Promise, jitter: function(number): number}} [retryOptions] - withRetry's third argument; the tests give an instant sleep
 * @returns {Promise<boolean>} whether the map is a favourite now
 * @throws {ApiError|NoResponseError} the failure of the request, once the policy has no retry left for it
 */
export const toggleFavourite = async (projectId, name, retryOptions = {}) => withRetry(() => MapRepository.toggleFavourite(projectId, name), ONE_RETRY_LOCKED, retryOptions);

/**
 * Reads the map's undo list (API 1.4), newest entry first.
 * @param {string} projectId
 * @param {string} name - the name of the map
 * @returns {Promise<{id: string, batchId: string, axis: ?string, createdAt: string, undoable: boolean, batchSize: number}[]>}
 */
export const getUndoList = async (projectId, name) => MapRepository.getUndoList(projectId, name);

/**
 * Reverts a map to before one undo entry (API 1.5) and builds the map the API answers with. A project that is busy (409 project_locked) is asked again
 * once, after a second; a request that got no response is never asked again: an undo is not idempotent (a second one would revert one more entry), and
 * the lost one may have been written. The answer is the map built from it, with the maps the undo affected and their flags (R46).
 * @param {string} projectId
 * @param {string} name - the name of the map
 * @param {string} entryId - the id of the entry, from getUndoList
 * @param {{sleep: function(number): Promise, jitter: function(number): number}} [retryOptions] - withRetry's third argument; the tests give an instant sleep
 * @returns {Promise<{map: LoadedMap, affectedMaps: string[], affectedMapsState: Object<string, boolean>, changedBytes: number}>}
 * @throws {ApiError|NoResponseError} the failure of the request, once the policy has no retry left for it
 * @throws {UnreadableMapError} when the answer holds no values for the uploaded file
 */
export const applyUndo = async (projectId, name, entryId, retryOptions = {}) => {
    const raw = await withRetry(() => MapRepository.applyUndo(projectId, name, entryId), ONE_RETRY_LOCKED, retryOptions);
    const {affectedMaps, affectedMapsState, changedBytes} = raw;

    return {map: buildMap(raw), affectedMaps, affectedMapsState, changedBytes};
};
